import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { startViewTransition } from '@theme/utilities';

/**
 * We fetch the full page (not the Section Rendering API) because
 * content_for_header — the only way to read custom query params in Liquid —
 * is only populated during full-page renders (the Section Rendering API
 * skips the layout, leaving content_for_header empty).
 *
 * The full-page HTML is then passed to morphSection, which extracts the
 * matching #shopify-section-{id} element and morphs it into the live DOM.
 */

class BrandCollectionTabsComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #fetchController = null;

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('brand-collection-tabs-component: section-id attribute is required');
    return id;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    window.addEventListener(
      'popstate',
      () => this.#fetchPageAndMorphSection(),
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#fetchController?.abort();
  }

  async #fetchPageAndMorphSection() {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const response = await fetch(window.location.href, {
      signal: this.#fetchController.signal,
    });
    const html = await response.text();
    await morphSection(this.sectionId, html);
  }

  handleTabClick(/** @type {MouseEvent} */ event) {
    if (Shopify.designMode) return;

    const button = /** @type {HTMLElement} */ (event.target)?.closest('[data-collection]');
    if (!button) return;

    const collection = button.dataset.collection;
    if (!collection) return;

    const bar = /** @type {HTMLElement | undefined} */ (this.refs.tabBar);

    // Bail if this tab is already active — no need to re-fetch.
    if (button.classList.contains('brand-tabs__tab--active')) return;

    const url = new URL(window.location.href);
    url.searchParams.set('collection', collection);
    url.searchParams.delete('page');

    history.pushState({}, '', url);

    // Immediately switch active tab visual state for instant feedback.
    const tabs = /** @type {Element[]} */ (this.refs.tabs ?? []);
    for (const tab of tabs) {
      const isActive = tab === button;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('brand-tabs__tab--active', isActive);
    }

    // Show loading overlay on product grid only.
    const grid = this.refs.productGrid;
    if (grid) grid.setAttribute('aria-busy', 'true');

    // Disable non-active tabs while products are loading.
    // tabBar has data-skip-node-update so morph won't clear this class.
    bar?.classList.add('brand-tabs__bar--loading');

    this.#fetchPageAndMorphSection().then(() => {
      bar?.classList.remove('brand-tabs__bar--loading');
      this.#collapseAccordions();
    });
  }

  handlePagination(data, event) {
    event.preventDefault();
    if (Shopify.designMode) return;

    const url = new URL(window.location.href);
    if (data?.collection) url.searchParams.set('collection', data.collection);
    if (data?.page) url.searchParams.set('page', data.page);

    history.pushState({}, '', url);

    const grid = this.refs.productGrid;
    if (grid) grid.setAttribute('aria-busy', 'true');

    const paginationNav = /** @type {HTMLElement | undefined} */ (this.refs.paginationNav);
    if (paginationNav) paginationNav.setAttribute('aria-busy', 'true');

    // Wait one rAF so the browser paints the aria-busy loading state before
    // startViewTransition captures its "before" screenshot. Without this,
    // the screenshot is taken before the dim is painted (same JS frame).
    requestAnimationFrame(() => {
      // startViewTransition returns Promise<void> resolving when
      // transition.finished resolves (after the crossfade animation ends).
      // We scroll AFTER that promise so:
      //   1. The "after" screenshot is captured at the current scroll position
      //      → the crossfade plays at the bottom where the user is watching.
      //   2. Smooth scroll runs as a distinct second phase once the new
      //      products are fully visible — "load here, then travel up".
      startViewTransition(
        () => this.#fetchPageAndMorphSection(),
        ['product-grid']
      ).then(() => {
        this.#collapseAccordions();
        this.#scrollToTabBar('smooth');
      });
    });
  }

  /**
   * Collapses all open accordion <details> elements inside the product grid.
   * Called after tab/page navigation so accordions reset to their default state.
   */
  #collapseAccordions() {
    const grid = this.refs.productGrid;
    if (!grid) return;
    for (const details of grid.querySelectorAll('details[open]')) {
      details.removeAttribute('open');
    }
  }

  /**
   * Scrolls so the tab bar sits just below the sticky header.
   *
   * Must be called with behavior:'instant' when inside a startViewTransition
   * callback, because:
   *  - smooth scroll is async and the promise resolves before the scroll
   *    completes, so the "after" screenshot is captured at the wrong position.
   *  - The view transition's own crossfade already provides visual smoothness.
   *
   * @param {'smooth' | 'instant'} [behavior]
   */
  #scrollToTabBar(behavior = 'instant') {
    const scrollRef = /** @type {HTMLElement | undefined} */ (this.refs.tabBar) ?? this;
    const stickyHeader = /** @type {HTMLElement | null} */ (document.querySelector('header'));
    const headerHeight = stickyHeader?.offsetHeight ?? 0;
    const rect = scrollRef.getBoundingClientRect();
    const targetScrollY = rect.top + window.scrollY - headerHeight - 8;
    window.scrollTo({ top: Math.max(0, targetScrollY), behavior });
  }

  handleTabKeydown(event) {
    const tabs = this.refs.tabs;
    if (!tabs?.length) return;

    const currentIndex = tabs.indexOf(event.target);
    if (currentIndex === -1) return;

    let newIndex;
    switch (event.key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    tabs[newIndex].focus();
    tabs[newIndex].click();
  }
}

if (!customElements.get('brand-collection-tabs-component')) {
  customElements.define('brand-collection-tabs-component', BrandCollectionTabsComponent);
}

// Theme editor: activate tab when block is selected
if (Shopify.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const component = event.target.closest('brand-collection-tabs-component');
    if (!component) return;
    const tab = event.target.closest('[role="tab"]');
    if (tab) tab.click();
  });
}
